/**
 * ContractGeneratorButton.tsx — gated trigger for umowa o roboty budowlane PDF (DOC-01).
 *
 * STRUCTURE follows ExportPDFButton.tsx verbatim:
 *   useState(generating) → useCallback async handler → dynamic import → await generateUmowaPDF
 *
 * GATE: isContractsLegallyApproved() drives label (approved / draft) and banner display.
 *   - When NOT approved: non-dismissible amber disclaimer banner shown (RESEARCH §DOC-03 Layer 3).
 *   - When approved: normal label, no banner (disclaimer footer still always in PDF).
 *
 * BUSINESS LOGIC: Zero logic here beyond orchestration.
 *   - Totals: computeEstimateTotals (shared parity authority — PDF-03).
 *   - Contract data: buildContractData (pure fn, price pass-through — PDF-03).
 *   - PDF generation: dynamic import pdfContractGenerator (font barrel out of main bundle).
 *
 * I18N: All visible strings use t('contract.key', 'PL fallback').
 */

import React, { useState, useCallback } from 'react';
import { computeEstimateTotals } from '@/entities/estimate/lib/computeTotals';
import { buildContractData } from '@/entities/document/lib/buildContractData';
import { isContractsLegallyApproved } from '@/features/contracts/lib/isContractsEnabled';
import type { Room } from '@/entities/room/model/types';
import type { ProjectMeta, EstimateItem } from '@/entities/estimate/model/types';
import type { ClientDetails, PaymentSchedule } from '@/shared/validation/schemas';
import i18n from '@/shared/config/i18n/i18n';

const t = (key: string, fallback: string) => i18n.t(key, fallback) as string;

export interface ContractGeneratorButtonProps {
  rooms: Room[];
  projectItems?: EstimateItem[];
  safetyMargin?: number;
  meta?: ProjectMeta;
  clientDetails?: ClientDetails;
  paymentSchedule?: PaymentSchedule;
  currency?: string;
  className?: string;
}

export function ContractGeneratorButton({
  rooms,
  projectItems = [],
  safetyMargin = 0,
  meta = {},
  clientDetails = { clientName: '', clientAddress: '', objectAddress: '' },
  paymentSchedule = { advancePercent: 0, finalPaymentDays: 14 },
  currency = 'PLN',
  className,
}: ContractGeneratorButtonProps) {
  const [generating, setGenerating] = useState(false);
  // CR-01 fix: render-time read is fine for UI label/banner only; the handler re-reads at
  // invocation time so a localStorage flip between render and click cannot produce a
  // mismatch. Mirror of the pattern used in ActEditor.tsx:170.
  const approved = isContractsLegallyApproved();

  const handleGenerate = useCallback(async () => {
    if (generating) return;
    setGenerating(true);
    try {
      // Re-read gate inside handler — avoids stale render-time snapshot (CR-01)
      const approvedAtClick = isContractsLegallyApproved();
      const vatRate = meta.vatRate ?? 0.23;
      // Compute totals via shared authority (PDF-03 cardinal rule — no second rounding path)
      const totals = computeEstimateTotals(rooms, projectItems, safetyMargin, vatRate);
      // Build contract data (pure fn — price is strict pass-through of totals)
      const contractData = buildContractData(meta, totals, clientDetails, paymentSchedule);
      // Dynamic import — keeps font barrel (~1.5MB) out of main bundle (ExportPDFButton pattern)
      const { generateUmowaPDF } = await import('@/entities/document/lib/pdfContractGenerator');
      await generateUmowaPDF(contractData, approvedAtClick, currency);
    } catch (err) {
      console.error(t('contract.export_error', 'Błąd generowania dokumentu'), err);
    } finally {
      setGenerating(false);
    }
  }, [rooms, projectItems, safetyMargin, meta, clientDetails, paymentSchedule, currency, generating]);

  return (
    <div className="flex flex-col gap-2">
      {/* Non-dismissible disclaimer banner — shown when DOC-03 gate not yet approved */}
      {!approved && (
        <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800">
          <p className="font-semibold">
            {t('contract.draft_warning_title', 'Wersja robocza — niezatwierdzona prawnie')}
          </p>
          <p className="mt-0.5 text-amber-700">
            {t(
              'contract.draft_warning_body',
              'Ten dokument jest wersją roboczą. Przed użyciem skonsultuj się z prawnikiem.',
            )}
          </p>
        </div>
      )}

      <button
        type="button"
        onClick={handleGenerate}
        disabled={generating}
        className={className}
        aria-busy={generating}
      >
        {generating
          ? t('contract.generating', 'Generowanie...')
          : approved
            ? t('contract.download_approved', 'Pobierz dokument')
            : t('contract.download_draft', 'Pobierz wersję roboczą')}
      </button>
    </div>
  );
}
